<script setup lang="ts">
  import { VueFinalModal } from 'vue-final-modal';
  import { RecordStageModal } from '@/entities/GeneratedRecord';
  import { DropzoneArea } from '@/features/Document';
  import { MAX_FILE_SIZE_MB } from '@/shared/config';
  import { useFileDropzone } from '@/shared/lib';
  import { ColoredButton, UiModalHeader } from '@/shared/ui';
  import { useUploadRecordPdf } from '../../model/useUploadRecordPdf';
  import UploadRecordPdfFileRow from './UploadRecordPdfFileRow.vue';

  const props = defineProps<{
    recordId: number;
    hasPdfResponse: boolean;
  }>();

  const emit = defineEmits<{
    close: [];
    refresh: [];
  }>();

  const REQUIREMENT_TEXT = `*O arquivo deve estar no formato PDF e ter até ${MAX_FILE_SIZE_MB} MB`;

  const {
    file,
    errorMessage,
    step,
    stages,
    loadingStages,
    submitting,
    choose,
    removeFile,
    requestSave,
    closeStage,
    confirmStage,
  } = useUploadRecordPdf({
    recordId: () => props.recordId,
    hasPdfResponse: () => props.hasPdfResponse,
    onDone: () => {
      emit('refresh');
      emit('close');
    },
    onStale: () => emit('refresh'),
  });

  const { fileInput, isDragging, openFileDialog, onDragEnter, onDragLeave, onDragOver, onDrop } =
    useFileDropzone();

  function onFilesDropped(event: DragEvent): void {
    choose(onDrop(event) ?? []);
  }

  function onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    choose(input.files ?? []);
    // Cleared so that choosing the same file again, after the trash, still fires `change`.
    input.value = '';
  }

  function requestClose(): void {
    if (loadingStages.value) return;
    emit('close');
  }
</script>

<template>
  <VueFinalModal
    v-if="step === 'file'"
    :model-value="true"
    class="flex items-center justify-center"
    content-class="flex w-[min(90vw,900px)] flex-col rounded-2xl border border-gray-02 bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="!loadingStages"
    :esc-to-close="!loadingStages"
    @update:model-value="!$event && requestClose()"
  >
    <div data-testid="upload-record-pdf-modal">
      <UiModalHeader
        class="border-b border-gray-02 py-3 pl-6 pr-3"
        title="Upload de Registro"
        text-color="gray-07"
        title-size="text-sm"
        @close="requestClose"
      />
      <div class="flex flex-col gap-1 border-b border-gray-02 px-6 py-8">
        <p class="text-sm font-semibold text-gray-07">
          Selecione o arquivo:
        </p>
        <p class="text-xs text-gray-05">
          {{ REQUIREMENT_TEXT }}
        </p>
        <input
          ref="fileInput"
          type="file"
          accept="application/pdf,.pdf"
          class="hidden"
          data-testid="upload-record-pdf-input"
          @change="onFileSelected"
        >
        <DropzoneArea
          v-if="file === null"
          class="mt-4"
          :is-dragging="isDragging"
          :on-drag-enter="onDragEnter"
          :on-drag-leave="onDragLeave"
          :on-drag-over="onDragOver"
          :on-drop="onFilesDropped"
          :open-file-dialog="openFileDialog"
          :multiple="false"
        />
        <UploadRecordPdfFileRow
          v-else
          class="mt-4"
          :name="file.name"
          @remove="removeFile"
        />
        <p
          v-if="errorMessage !== null"
          class="mt-2 text-xs text-red-16"
          role="alert"
          data-testid="upload-record-pdf-error"
        >
          {{ errorMessage }}
        </p>
      </div>
      <div class="flex justify-end gap-6 px-6 py-8">
        <ColoredButton
          outlined
          color="gray-07"
          border-color="gray-02"
          text="Cancelar"
          data-testid="upload-record-pdf-cancel"
          @click="requestClose"
        />
        <ColoredButton
          text="Salvar"
          :disabled="file === null"
          :loading="loadingStages"
          data-testid="upload-record-pdf-save"
          @click="requestSave"
        />
      </div>
    </div>
  </VueFinalModal>
  <RecordStageModal
    v-else
    :stages="stages"
    :submitting="submitting"
    @close="closeStage"
    @confirm="confirmStage"
  />
</template>
